import { SIDEBAR_NAV } from '@/constants/navigation'
import { ROUTES } from '@/constants/routes'
import type { AuthUser } from '@/types/auth.types'
import type { PermissionKey } from '@/types/permission.types'
import { canAccess } from '@/utils/permission.utils'

function userCan(user: AuthUser, key: PermissionKey): boolean {
  return canAccess(user.role, user.permissions, key)
}

function firstSidebarPath(user: AuthUser): string | null {
  for (const entry of SIDEBAR_NAV) {
    if (entry.type === 'item') {
      if (!entry.permission || userCan(user, entry.permission)) {
        return entry.href
      }
      continue
    }

    const child = entry.children.find(
      (c) => !c.permission || userCan(user, c.permission),
    )
    if (child) return child.href
  }
  return null
}

/**
 * Preferred reports page for the user.
 * Order: Dispatches → Purchases → Exports → Dashboard → first nav item → Workspace.
 */
export function getReportsLandingPath(
  user?: AuthUser | null,
  /** @param options.breakdowns — legacy name for the old Category tab. The
   * page it pointed to is gone; this now lands on the Trend page instead of
   * 404ing a bookmarked `/reports/summary/breakdowns` link. */
  options?: { breakdowns?: boolean },
): string {
  const breakdowns = options?.breakdowns === true

  if (!user) {
    return ROUTES.WORKSPACE
  }

  if (userCan(user, 'dispatch_reports.view')) {
    return breakdowns
      ? ROUTES.REPORTS_DISPATCH_TREND
      : ROUTES.REPORTS_DISPATCH_SUMMARY
  }
  if (userCan(user, 'purchase_reports.view')) {
    return breakdowns
      ? ROUTES.REPORTS_PURCHASE_TREND
      : ROUTES.REPORTS_PURCHASE_SUMMARY
  }
  if (userCan(user, 'reports.view')) {
    return ROUTES.REPORTS_EXPORTS
  }
  if (userCan(user, 'dashboard.view')) {
    return ROUTES.DASHBOARD
  }

  return firstSidebarPath(user) ?? ROUTES.WORKSPACE
}

/**
 * Preferred landing path for the user — Dashboard whenever they can open it
 * (all devices), otherwise their preferred reports page. Unauthenticated or
 * "want" path — never used as a Guard target without canOpenPath.
 */
function preferredLandingPath(user: AuthUser): string {
  if (userCan(user, 'dashboard.view')) return ROUTES.DASHBOARD
  return getReportsLandingPath(user)
}

/** Exact permission for a known path (reports paths must not OR together). */
function permissionForPath(path: string): PermissionKey | PermissionKey[] | null {
  switch (path) {
    case ROUTES.DASHBOARD:
      return 'dashboard.view'
    case ROUTES.REPORTS_DISPATCH_SUMMARY:
    case ROUTES.REPORTS_DISPATCH_TREND:
      return 'dispatch_reports.view'
    case ROUTES.REPORTS_PURCHASE_SUMMARY:
    case ROUTES.REPORTS_PURCHASE_TREND:
      return 'purchase_reports.view'
    case ROUTES.REPORTS_EXPORTS:
      return 'reports.view'
    case ROUTES.REPORTS:
      return [
        'dispatch_reports.view',
        'purchase_reports.view',
        'reports.view',
      ]
    case ROUTES.DISPATCH_SHEET:
      return 'delivery_challans.view'
    case ROUTES.PURCHASE_SHEET:
      return 'goods_received.view'
    default:
      return null
  }
}

const OPEN_AUTHENTICATED_PREFIXES = [
  ROUTES.WORKSPACE,
  ROUTES.ACCOUNT,
  ROUTES.NOTIFICATIONS,
  ROUTES.MORE,
  ROUTES.MOBILE_SHARE,
] as const

/** Pathname only so post-login returnTo can keep ?query from a share/deep link. */
function pathnameOnly(path: string): string {
  const trimmed = path.trim()
  if (!trimmed.startsWith('/') || trimmed.startsWith('//')) return trimmed
  const noHash = trimmed.split('#')[0] ?? trimmed
  const queryAt = noHash.indexOf('?')
  return queryAt === -1 ? noHash : noHash.slice(0, queryAt)
}

export function canOpenPath(user: AuthUser, path: string): boolean {
  if (user.role === 'super_admin') return true
  const normalized = pathnameOnly(path)

  if (
    OPEN_AUTHENTICATED_PREFIXES.some(
      (root) => normalized === root || normalized.startsWith(`${root}/`),
    )
  ) {
    return true
  }

  const mapped = permissionForPath(normalized)
  if (mapped) {
    const keys = Array.isArray(mapped) ? mapped : [mapped]
    return keys.some((key) => userCan(user, key))
  }

  for (const entry of SIDEBAR_NAV) {
    if (entry.type === 'item') {
      if (entry.href !== normalized) continue
      return entry.permission ? userCan(user, entry.permission) : true
    }

    for (const child of entry.children) {
      if (child.href !== normalized) continue
      return child.permission ? userCan(user, child.permission) : true
    }
  }

  return false
}

export function getFirstAccessibleHomePath(user?: AuthUser | null): string {
  if (!user) return ROUTES.WORKSPACE

  const preferred = preferredLandingPath(user)
  if (canOpenPath(user, preferred)) return preferred

  return firstSidebarPath(user) ?? ROUTES.WORKSPACE
}

const IGNORE_INTENDED_PATHS = new Set<string>([
  ROUTES.DASHBOARD,
  ROUTES.LOGIN,
  ROUTES.FORBIDDEN,
  ROUTES.UNAUTHORIZED,
])

function isSafeInAppPath(path: string): boolean {
  return path.startsWith('/') && !path.startsWith('//') && !path.startsWith('/\\')
}

/** Resolve post-login destination; prefer intended route when accessible, otherwise first accessible page. */
export function resolveHomePath(
  user?: AuthUser | null,
  intended?: string | null,
): string {
  if (
    user &&
    intended &&
    isSafeInAppPath(intended) &&
    !IGNORE_INTENDED_PATHS.has(pathnameOnly(intended)) &&
    canOpenPath(user, intended)
  ) {
    return intended
  }

  return getFirstAccessibleHomePath(user)
}

/** Where to send a user who opened a page they cannot access. */
export function getPermissionDeniedPath(
  user: AuthUser | null | undefined,
  currentPath: string,
): string {
  const home = getFirstAccessibleHomePath(user)
  if (home !== currentPath) return home
  return ROUTES.FORBIDDEN
}

/** Mobile bottom-nav roots — self-navigating sections that never show a back button. */
export function isMobileBackRoot(pathname: string): boolean {
  return (
    pathname === ROUTES.DASHBOARD ||
    pathname === ROUTES.MORE ||
    pathname.startsWith(ROUTES.REPORTS) ||
    pathname.startsWith(ROUTES.WORKSPACE) ||
    pathname.startsWith(ROUTES.ACCOUNT)
  )
}

/** List routes whose detail/create/edit children should step back to the list, not all the way to Workspace. */
const BACK_FALLBACK_LIST_ROUTES: string[] = [
  ROUTES.CUSTOMERS,
  ROUTES.SUPPLIERS,
  ROUTES.CATEGORIES,
  ROUTES.SALES_LEADS,
  ROUTES.DISPATCH_SHEET,
  ROUTES.PURCHASE_SHEET,
  ROUTES.USERS,
  ROUTES.TASKS,
]

/**
 * Safe mobile back-navigation target when there's no in-app history to pop —
 * a deep link, a refresh, or the first screen this session. Never points at
 * a route the current user could be denied on: Workspace and the Settings
 * hub are always open to any authenticated user, and list routes are the
 * same guard as their own detail/create/edit children.
 */
export function resolveMobileBackFallback(pathname: string, user?: AuthUser | null): string {
  if (pathname === ROUTES.SETTINGS || pathname.startsWith(`${ROUTES.SETTINGS_SHEETS_SYNC}`)) {
    return ROUTES.ACCOUNT
  }

  for (const listRoute of BACK_FALLBACK_LIST_ROUTES) {
    if (pathname.startsWith(`${listRoute}/`)) {
      if (user && !canOpenPath(user, listRoute)) return ROUTES.WORKSPACE
      // A bare /sales/leads opens Summary — a lead's page steps back to the Queries list.
      return listRoute === ROUTES.SALES_LEADS ? `${listRoute}?tab=lead` : listRoute
    }
  }

  return ROUTES.WORKSPACE
}
